import React, { useState } from 'react';
import {
    Box,
    Flex,
    Button,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider,
    Text,
    Badge,
    Icon,
    IconButton,
    useColorMode,
    useColorModeValue,
    Tooltip,
    VStack,
    HStack,
    Spacer
} from '@chakra-ui/react';
import {
    MdMenu,
    MdNotifications,
    MdSettings,
    MdLogout,
    MdPeople,
    MdPerson,
    MdLightMode,
    MdDarkMode,
    MdRefresh
} from 'react-icons/md';
import { AuthService } from '../../services/authService';
import { formatDateTime, getRelativeTime } from '../../utils/helpers';
import UserAvatar from '../Common/UserAvatar';
import toast from 'react-hot-toast';

/**
 * Navigation Bar Component
 */
function Navbar({ 
    onOpen, 
    userProfile, 
    notifications = [], 
    unreadCount = 0, 
    newToastedAlertsCount = 0,
    issuesStats,
    sidebarVisible = true,
    onNotificationClick, 
    onNotificationsView,
    appConfig,
    ...rest 
}) {
    const { colorMode, toggleColorMode } = useColorMode();
    const [isRefreshing, setIsRefreshing] = useState(false);
    
    // Theme-aware colors
    const navbarBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const textColor = useColorModeValue('gray.700', 'gray.200');
    const mutedColor = useColorModeValue('gray.500', 'gray.400');

    /**
     * Handle user logout with confirmation
     * Shows logout confirmation and processes logout
     */
    const handleLogout = async () => {
        try {
            await AuthService.logout(true);
        } catch (error) {
            console.error('Logout failed:', error);
            toast.error('Logout failed. Please try again.');
        }
    };

    /**
     * Handle profile settings navigation
     * Placeholder for future profile management functionality
     */
    const handleProfileSettings = () => {
        toast('Profile settings coming soon!', {
            icon: '⚙️',
            duration: 3000
        });
    };

    /**
     * Handle page refresh
     * Triggers page reload to refresh data
     */
    const handleRefresh = async () => {
        setIsRefreshing(true);
        
        try {
            // Show refresh toast
            toast('Refreshing dashboard...', {
                icon: '🔄',
                duration: 2000
            });
            
            // Simulate refresh delay for UX
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Reload the page to refresh all data
            window.location.reload();
            
        } catch (error) {
            toast.error('Refresh failed. Please try again.');
        } finally {
            setIsRefreshing(false);
        }
    };

    /**
     * Handle hamburger menu click
     * Properly opens the mobile sidebar drawer
     */
    const handleMenuClick = () => {
        if (onOpen) {
            onOpen();
        } else {
            console.error('onOpen function not provided to Navbar');
        }
    };

    const getColourScheme = (notification) =>{
        if(notification.status === "issue") return 'red';
        if(notification.status ==="success") return 'green';
        if(notification.status === "error") return 'red';
        if(notification.status === "warning") return'orange';
        if(notification.status === "info") return 'blue';
        if(notification.status === "critical") return 'red';
    }

    /**
     * Notification Item Component
     * Renders individual notifications in the dropdown menu
     */
    const NotificationItem = ({ notification }) => (
        <MenuItem
            onClick={() => onNotificationClick?.(notification)}
            p={3}
            _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}
        >
            <VStack align="start" spacing={1} w="full">
                <HStack justify="space-between" w="full">
                    <Text fontSize="sm" fontWeight={notification.isRead ? 'normal' : 'semibold'}>
                        {notification.category || 'Notification'}
                    </Text>
                    <Text fontSize="xs" color={mutedColor}>
                        {notification.timeAgo || getRelativeTime(notification.createdAt)}
                    </Text>
                </HStack>
                <Text 
                    fontSize="xs" 
                    color={mutedColor}
                    noOfLines={2}
                    w="full"
                >
                    {notification.message}
                </Text>
                {notification.status && (
                    <Badge
                        size="sm"
                        // colorScheme={notification.status === 'error' ? 'red' : 'green'}
                        colorScheme={getColourScheme(notification)}
                        variant="subtle"
                    >
                        {notification.status}
                    </Badge>
                )}
            </VStack>
        </MenuItem>
    );

    return (
        <Box
            as="nav"
            bg={navbarBg}
            borderBottom="1px"
            borderBottomColor={borderColor}
            position="sticky"
            top={0}
            zIndex={1000}
            {...rest}
        >
            <Flex
                h={16}
                alignItems="center"
                px={{ base: 4, md: 6 }}
                w="full"
            >
                {/* Left side - Mobile menu button and title */}
                <HStack spacing={4} flex="0 0 auto">
                    <IconButton
                        onClick={handleMenuClick}
                        variant="ghost"
                        aria-label="Toggle navigation menu"
                        icon={<MdMenu />}
                        size="lg"
                        _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
                    />

                    <Text
                        display={{ 
                            base: 'block', 
                            lg: sidebarVisible ? 'none' : 'block' 
                        }}
                        fontSize="lg"
                        fontWeight="bold"
                        color={textColor}
                        noOfLines={1}
                    >
                        Prism Monitor
                    </Text>
                </HStack>

                {/* Spacer to push right side controls to the end */}
                <Spacer />

                {/* Right side - Controls and user menu - moved to absolute right */}
                <HStack spacing={3} flex="0 0 auto">
                    {/* Refresh Button */}
                    <Tooltip label="Refresh Dashboard" hasArrow placement="bottom">
                        <IconButton
                            variant="ghost"
                            aria-label="Refresh dashboard"
                            icon={<MdRefresh />}
                            onClick={handleRefresh}
                            isLoading={isRefreshing}
                            size="md"
                            _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
                        />
                    </Tooltip>

                    {/* Theme Toggle */}
                    <Tooltip 
                        label={`Switch to ${colorMode === 'light' ? 'dark' : 'light'} theme`} 
                        hasArrow
                        placement="bottom"
                    >
                        <IconButton
                            variant="ghost"
                            aria-label="Toggle color mode"
                            icon={colorMode === 'light' ? <MdDarkMode /> : <MdLightMode />}
                            onClick={toggleColorMode}
                            size="md"
                            _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
                        />
                    </Tooltip>

                    {/* Notifications Menu */}
                    <Menu>
                        <Tooltip label="Notifications" hasArrow placement="bottom">
                            <MenuButton
                                as={IconButton}
                                variant="ghost"
                                aria-label="Open notifications"
                                size="md"
                                _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
                                icon={
                                    <Box position="relative">
                                        <Icon 
                                            as={MdNotifications} 
                                            w={5} 
                                            h={5}
                                            color={useColorModeValue('brand.500', 'currentColor')}
                                            css={{
                                                animation: newToastedAlertsCount > 0 ? 'bellRing 0.5s ease-in-out infinite alternate' : 'bellGlow 3s ease-in-out infinite',
                                                transformOrigin: 'top center',
                                                '@keyframes bellRing': {
                                                    '0%': {
                                                        transform: 'rotate(-10deg)',
                                                        filter: 'drop-shadow(0 0 3px #ffd700)',
                                                    },
                                                    '100%': {
                                                        transform: 'rotate(10deg)',
                                                        filter: 'drop-shadow(0 0 6px #ffd700)',
                                                    }
                                                },
                                                '@keyframes bellGlow': {
                                                    '0%, 100%': {
                                                        filter: 'drop-shadow(0 0 0px currentColor)',
                                                        transform: 'scale(1)',
                                                    },
                                                    '50%': {
                                                        filter: 'drop-shadow(0 0 4px currentColor)',
                                                        transform: 'scale(1.05)',
                                                    }
                                                }
                                            }}
                                        />
                                        {newToastedAlertsCount > 0 && (
                                            <Badge
                                                position="absolute"
                                                top="-6px"
                                                right="-6px"
                                                colorScheme="red"
                                                borderRadius="full"
                                                fontSize="2xs"
                                                minW="16px"
                                                h="16px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                fontWeight="bold"
                                                css={{
                                                    animation: 'badgePulse 1s ease-in-out infinite',
                                                    '@keyframes badgePulse': {
                                                        '0%, 100%': {
                                                            transform: 'scale(1)',
                                                        },
                                                        '50%': {
                                                            transform: 'scale(1.2)',
                                                        }
                                                    }
                                                }}
                                            >
                                                {newToastedAlertsCount > 99 ? '99+' : newToastedAlertsCount}
                                            </Badge>
                                        )}
                                    </Box>
                                }
                            />
                        </Tooltip>
                        <MenuList maxW="350px" p={0} border="1px" borderColor={borderColor}>
                            <Box p={3} borderBottom="1px" borderBottomColor={borderColor}>
                                <HStack justify="space-between">
                                    <Text fontWeight="semibold" fontSize="sm">
                                        Notifications
                                    </Text>
                                    {newToastedAlertsCount > 0 && (
                                        <Badge colorScheme="red" variant="subtle" fontSize="xs">
                                            {newToastedAlertsCount} new
                                        </Badge>
                                    )}
                                </HStack>
                            </Box>
                            
                            {notifications.length > 0 ? (
                                <>
                                    {notifications.slice(0, 3).map((notification) => (
                                        <NotificationItem
                                            key={notification.id}
                                            notification={notification}
                                        />
                                    ))}
                                    
                                    <MenuDivider />
                                    <MenuItem
                                        onClick={onNotificationsView}
                                        textAlign="center"
                                        color="brand.500"
                                        fontWeight="medium"
                                        justifyContent="center"
                                    >
                                        View All Notifications
                                    </MenuItem>
                                </>
                            ) : (
                                <Box p={6} textAlign="center">
                                    <Text color={mutedColor} fontSize="sm">
                                        No notifications
                                    </Text>
                                </Box>
                            )}
                        </MenuList>
                    </Menu>

                    {/* User Menu */}
                    <Menu>
                        <Tooltip label="User menu" hasArrow placement="bottom">
                            <MenuButton
                                as={Button}
                                variant="ghost"
                                size="md"
                                _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
                                leftIcon={
                                    <UserAvatar
                                        userProfile={userProfile}
                                        size="sm"
                                        showOnlineIndicator={false}
                                    />
                                }
                                pl={2}
                                pr={3}
                            >
                                {/* User name only visible on larger screens */}
                                <Box 
                                    textAlign="left" 
                                    display={{ base: 'none', md: 'block' }}
                                    ml={2}
                                >
                                    <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                                        {userProfile?.name || 'Admin User'}
                                    </Text>
                                    <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                                        {userProfile?.role || 'Administrator'}
                                    </Text>
                                </Box>
                            </MenuButton>
                        </Tooltip>
                        
                        <MenuList border="1px" borderColor={borderColor}>
                            {/* User Info Section */}
                            <Box p={3} borderBottom="1px" borderBottomColor={borderColor}>
                                <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                                    {userProfile?.name || 'Admin User'}
                                </Text>
                                <Text color={mutedColor} fontSize="xs" noOfLines={1}>
                                    {userProfile?.email}
                                </Text>
                                <Text color={mutedColor} fontSize="xs">
                                    Role: {userProfile?.role || 'Administrator'}
                                </Text>
                                {localStorage.getItem('loginTime') && (
                                    <Text color={mutedColor} fontSize="xs">
                                        Logged in: {formatDateTime(new Date(localStorage.getItem('loginTime')), { showDate: false })}
                                    </Text>
                                )}
                            </Box>

                            {/* Menu Items */}
                            {/* <MenuItem
                                icon={<MdPerson />}
                                onClick={handleProfileSettings}
                                fontSize="sm"
                            >
                                Profile Settings
                            </MenuItem> */}
                            
                            <MenuItem
                                icon={<MdNotifications />}
                                onClick={onNotificationsView}
                                fontSize="sm"
                            >
                                <HStack justify="space-between" w="full">
                                    <Text>All Notifications</Text>
                                    {newToastedAlertsCount > 0 && (
                                        <Badge colorScheme="red" variant="subtle" fontSize="xs">
                                            {newToastedAlertsCount}
                                        </Badge>
                                    )}
                                </HStack>
                                
                            </MenuItem>
                            <MenuItem
                                icon={<MdPeople/>}
                                onClick={() => window.open('https://teams.microsoft.com/l/channel/19%3A8n3rrNzAuKmASiUl3A1yU2lZTveXX2hh7jfcQn5orbc1%40thread.tacv2/Prism%20Monitor?groupId=4eb16203-191d-4fdf-8611-411593b35fcd&tenantId=c2033d23-79fc-409a-846e-85af78077e71', '_blank')}
                                fontSize="sm"
                            >
                                <HStack justify="space-between" w="full">
                                    <Text>Go to Teams Channel</Text>
                                </HStack>
                                
                            </MenuItem>
                            
                            {/* <MenuItem
                                icon={<MdSettings />}
                                onClick={handleProfileSettings}
                                fontSize="sm"
                            >
                                Settings
                            </MenuItem>
                             */}
                            <MenuDivider />
                            
                            <MenuItem
                                icon={<MdLogout />}
                                onClick={handleLogout}
                                color="red.500"
                                fontSize="sm"
                                _hover={{ bg: 'red.50', color: 'red.600' }}
                            >
                                Sign Out
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </HStack>
            </Flex>
        </Box>
    );
}

export default Navbar;